import throttle from 'lodash-es/throttle';
import { createEmpty, extend, isEmpty } from 'ol/extent';
import Map from 'ol/Map';
import { unByKey } from 'ol/Observable';
import Projection from 'ol/proj/Projection';
import View from 'ol/View';
import isEqual from 'lodash-es/isEqual';
import { getActionContext } from 'cadenza/action-context';
import { errorAlert } from 'cadenza/alert';
import { getSelectedLayerFeatures } from 'cadenza/api-client/maps-api';
import { setupViewActivation } from 'cadenza/api-client/views-api';
import { isResettableMapInteraction } from 'cadenza/map/actions/map-interaction-action';
import { HighlightHandler } from 'cadenza/map/highlight-layer/highlight-handler';
import {
  createLayer,
  refreshLayer,
  scaleToResolution,
  updateLayer,
  WORKBOOK_VIEW_ON_DEMAND
} from 'cadenza/map/layer-utils';
import { MapProgressSpinner } from 'cadenza/map/map-progress-spinner';
import {
  calculateMapViewPadding,
  getControl,
  resolutionForScale,
  scaleForResolution
} from 'cadenza/map/map-utils';
import { MapView } from 'cadenza/map/map-view';
import i18n from 'cadenza/map/messages.properties';
import { getWebMercatorMaxResolution, isWebMercatorProjection } from 'cadenza/map/proj-utils';
import { assert } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import { roundToFloatOrInt } from 'cadenza/utils/math';
import { MapContextApi } from 'cadenza/api-client/map-context-api';
import { SourceProperty } from 'cadenza/map/source-property';
import { on } from 'cadenza/utils/event-util';
import { WorkbookHighlightHandler } from 'cadenza/map/custom/workbook/workbook-highlight-handler';
import { cadenzaSessionStorage } from 'cadenza/utils/session-storage/session-storage';

import { context } from './context';
import { LayerProperty } from './layer-property';
import { MapProperty } from './map-property';
import { ViewProperty } from './view-property';

const DEFAULT_NUMBER_OF_ZOOM_LEVELS = 25;
const ZOOM_SCALE_FACTOR = 2;

/**
 * There is only one selected layer at a time. On the client, we maintain this info as a property of the map, not of the layer.
 * That's why we do not add this to cadenza/map/layer-property."
 */
const LAYER_SELECTED_PROPERTY = 'selected';

function toggleGroupActiveLayerChanged (existingLayer, layerProps) {
  const previouslyActive = existingLayer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
  const currentlyActive = layerProps[LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX];
  return previouslyActive && (previouslyActive !== currentlyActive);
}

/**
 * Please do *not* instantiate this directly and instead use cadenza/map/context, i.e. context(map)
 */
export class MapContext {

  #mapContextApi;
  #contextPushEnabled;
  /**
   * List of IDs of views or data views that need to be invalidated.
   *
   * @type {string[]|null|undefined}
   */
  changedLayerIds;

  constructor (map) {
    assert(map instanceof Map, 'map must be an instance of ol/Map');
    this._map = map;
    const mapId = map.get(MapProperty.ID);
    this.#mapContextApi = new MapContextApi({ mapId });
    this._mapProgressSpinner = getControl(map, MapProgressSpinner);
    this._eventSource = null;
    this._highlightHandler = map.get(MapProperty.IS_WORKBOOK_MAP)
      ? new WorkbookHighlightHandler(map)
      : new HighlightHandler(map);
    this.#contextPushEnabled = map.get(MapProperty.CONTEXT_PUSH_ENABLED);

    this._onVisibilityChange = () => {
      if (!document.hidden) {
        this._updateCurrentMap();
      }
    };
  }

  /**
   * Initial setup of map context
   *
   * @param mapExtent - optional override of map extent obtained from server
   * @return promise with the result of the initial map pull
   */
  setup (mapExtent) {
    const setupPromise = this.pull(mapExtent);

    const map = this._map;
    this._eventKeys = [
      map.on('movestart', () => {
        this._zoomLevelBeforeMove = map.getView().getZoom();
      }),
      map.on('moveend', () => {
        const pushPromise = this.push();

        // If the map is zoomed, the selection count of a group theme can change if a feature layer
        // in the group theme becomes visible/invisible at the new scale.
        if (map.getView().getZoom() !== this._zoomLevelBeforeMove) {
          this._onZoomChange(pushPromise);
        }
      }),
      map.on('propertychange', (event) => {
        if (event.key === MapProperty.SELECTED_LAYER || event.key === MapProperty.SIZE) {
          this.push();
        }
        if (event.key === MapProperty.PRIMARY_INTERACTION) {
          if (event.oldValue) {
            event.oldValue.setActive(false);
            map.removeInteraction(event.oldValue);
          }
          const newInteraction = map.get(MapProperty.PRIMARY_INTERACTION);
          if (newInteraction) {
            map.addInteraction(newInteraction);
            newInteraction.setActive(true);
          }
        }
      }),
      map.getLayers().on('propertychange', () => {
        const selectedLayer = map.get(MapProperty.SELECTED_LAYER);
        if (!this.layers.includes(selectedLayer)) {
          map.unset(MapProperty.SELECTED_LAYER);
        }
        this.push();
      }),
      map.getLayers().on('remove', ({ element: layer }) => context(layer).teardown())
    ];
    if (this.#contextPushEnabled) {
      this._updateCurrentMap();
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      setupViewActivation(map.get(MapProperty.ID), true);
    }

    return setupPromise;
  }

  _onZoomChange (pushPromise) {
    const selectedLayer = this._map.get(MapProperty.SELECTED_LAYER);
    if (selectedLayer && selectedLayer.get(LayerProperty.IS_GROUP_THEME) && selectedLayer.get(LayerProperty.HAS_FEATURES)) {
      pushPromise
        .then(() => {
          const srsCode = this._map.getView().getProjection().getCode();
          return getSelectedLayerFeatures(layerId(selectedLayer), srsCode, this.mapScale);
        })
        .then(({ features }) => {
          /*
           * When Cadenza is embedded in a businessApp we need to forward the new selection to the businessAp
           *
           * The issue is: OL BaseObject.prototype.set sets the new value and sends notifications whenever oldValue !== newValue,
           * Since [] !== [] is always true, we send notifications on every zoom even if nothing is selected!
           */
          const newSelectedFeatureIDs = features.map(({ id }) => id);
          const hasSelectionChanged = !isEqual(selectedLayer.get(LayerProperty.SELECTED_FEATURE_IDS), newSelectedFeatureIDs);
          if (hasSelectionChanged) {
            selectedLayer.set(LayerProperty.SELECTED_FEATURE_IDS, newSelectedFeatureIDs);
          }
        })
        .catch((error) => logger.error('Failed to refresh selection count', error));
    }
  }

  _updateCurrentMap () {
    cadenzaSessionStorage.setItem('currentMapId', this._map.get(MapProperty.ID));
  }

  teardown () {
    unByKey(this._eventKeys);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
  }

  /**
   * Loads map metadata from the server and updates the map accordingly.
   *
   * @param mapExtent - optional override of map extent obtained from server
   * @return promise that resolves when the pull is finished
   */
  pull (mapExtent) {
    const map = this._map;
    // AK: skipping image loading in layer-utils/imageLoadFunction leads to spinner not disappearing
    // (MapProgressSpinner._totalCount() === 1)
    this._mapProgressSpinner.show();
    const pull = this.#mapContextApi.getContext(map.get(MapProperty.CONTEXT_URL), map.getInnerSize())
      .then(({ title, tags = [], projection, currentExtent, maxExtent, layers, minAllowedScale, maxAllowedScale, constrainZoomScale, forceMinMaxScale }) => {
        this._isPullInProgress = true;
        map.setProperties({ title, tags });
        const viewIsInitialized = !!map.getView().getCenter();
        if (!viewIsInitialized) {
          this._initializeView({
            projection,
            currentExtent: mapExtent || currentExtent,
            maxExtent,
            minAllowedScale,
            maxAllowedScale,
            constrainZoomScale,
            forceMinMaxScale
          });
          this._scheduleViewExtentReinitializationIfRequired(mapExtent, minAllowedScale, maxAllowedScale);
        }

        this._updateLayers(layers);
        this._registerMapStatusChangeEmitter();
      });
    pull
      .catch(error => logger.error('Failed to pull map context', error))
      .finally(() => {
        this._isPullInProgress = false;
        this._mapProgressSpinner.hide();
      });
    return pull;
  }

  _registerMapStatusChangeEmitter () {
    const versionedRepoLayerLoaded = this.layers.some(layer => layer.get(LayerProperty.IS_VERSION_THEME));
    if (versionedRepoLayerLoaded) {
      if (!this._eventSource || this._eventSource.readyState === EventSource.CLOSED) {
        this._initializeEventSource();
      }
    } else {
      if (this._eventSource) {
        this._eventSource.close();
        this._eventSource = null;
      }
    }
  }

  _initializeEventSource () {
    this._eventSource = this.#mapContextApi.createMapStatusEventSource();
    this._eventSource.addEventListener('map-status', this._handleMapStatusUpdate.bind(this));
  }

  _handleMapStatusUpdate () {
    errorAlert(i18n('context.update.message.title'), i18n('context.update.message.text'));
    this.pull();
  }

  _initializeView ({
    projection: { code, units, title, usesDefaultSrs },
    currentExtent,
    maxExtent,
    minAllowedScale,
    maxAllowedScale,
    constrainZoomScale,
    forceMinMaxScale
  }) {
    const map = this._map;
    const projection = new Projection({ code, units });
    const { minResolution, maxResolution } = calculateMinMaxResolutions(projection, maxExtent, map.getInnerSize(), minAllowedScale, maxAllowedScale);
    const padding = calculateMapViewPadding(map);

    const mapOptions = {
      projection,
      extent: maxExtent,
      maxResolution,
      minResolution,
      padding,
      // By default the whole view extent is constrained to the given extent, so the user is not allowed to see anything outside of the given extent.
      // Thus always only some part of the given extent is visible. But our aim here is to display the whole given extent in the view.
      // With constrainOnlyCenter = true, the extent constraint is applied to the view center and not the whole extent.
      constrainOnlyCenter: true,
      smoothResolutionConstraint: false,
      smoothExtentConstraint: false,
      constrainResolution: !!constrainZoomScale
    };

    const minAllowedScaleSet = isMinAllowedScaleSet(minAllowedScale);
    const maxAllowedScaleSet = isMaxAllowedScaleSet(maxAllowedScale);

    if (forceMinMaxScale && minResolution && maxResolution && (minAllowedScaleSet || maxAllowedScaleSet)) {
      // Forces open layers to really use defined resolution steps exactly.
      // This settings wins over minResolution and maxResolution options, if resolutions option is defined OL ignores min/maxResolution options.
      // Without this setting min would be inferred from max and probably not the same value as the configured min.
      // Resolution steps are calculated from the min and max resolution values, starting with max and dividing it with 2 till min is reached, adding also min at the end.
      mapOptions.resolutions = this._calcResolutions(maxResolution, minResolution);
    }

    const view = new MapView(mapOptions);
    view.set(ViewProperty.MAX_EXTENT, maxExtent);
    view.set(ViewProperty.PROJECTION_TITLE, title);
    view.set(ViewProperty.USES_DEFAULT_SRS, usesDefaultSrs);

    // Do view.fit() *before* map.setView(view)
    // so that the view already has a center when set.
    // This avoids initialization problems, e.g. with the OverviewMap.
    //
    // "nearest: true" is required to avoid zooming one step out after page refresh.
    // If exactly same extent is given to the view with exactly same size, the resolution
    // calculated by OL is not exactly the same as it was, because of rounding errors.
    // "nearest" ensures, that the best suitable constrained resolution will be set and the map will not be zoomed out to fit the extent.
    view.fit(currentExtent, { nearest: true, size: map.getInnerSize() });
    map.setView(view);
  }

  _calcResolutions (maxResolution, minResolution) {
    const resolutions = [];
    resolutions.push(maxResolution);
    let half = roundToFloatOrInt(maxResolution / ZOOM_SCALE_FACTOR, 3);
    while (half > minResolution) {
      resolutions.push(half);
      half = roundToFloatOrInt(half / ZOOM_SCALE_FACTOR, 3);
    }
    resolutions.push(minResolution);
    return resolutions;
  }

  // A workbook map view can be initially hidden on mobile devices, where only one view is shown at a time.
  // Unfortunately all hidden workbook views are still fully initialized and do all their internal work, even if they are hidden.
  // So the map view can be initialized even if it is not displayed. In this case map has a size [0, 0], OL defaults it then to [100, 100].
  // We cannot initialize the view extent without knowing the exact map container size.
  // A workaround here is to wait for the first resize event (it means, the view was shown) and reinitialize the view extent.
  // A perfect solution would be to not load anything in the hidden views until they are shown. Then this method can be cleaned up.
  _scheduleViewExtentReinitializationIfRequired (mapExtent, minAllowedScale, maxAllowedScale) {
    const map = this._map;
    const { width, height }  = map.getTarget().getBoundingClientRect();
    if (width !== 0 && height !== 0) {
      return;
    }
    // Map size 0 usually means the map is not visible (for example because the whole workbook map view is not displayed),
    // in this case we schedule map current extent recalculation and reset it as soon as window is resized.
    on(window, 'resize', () => {
      this._mapProgressSpinner.show();
      // mapExtent is the persisted last extent. In case we have mapExtent, just use it. Otherwise request the extent from the backend (by doing a lightweight version of pull request)
      const getMapExtentPromise = mapExtent
        ? Promise.resolve(mapExtent)
        : this.#mapContextApi.getContext(map.get(MapProperty.CONTEXT_URL), map.getInnerSize())
          .then(({ currentExtent }) => currentExtent);
      getMapExtentPromise
        .then(currentExtent => this._reinitializeViewExtent(currentExtent, minAllowedScale, maxAllowedScale))
        .catch(error => logger.error('Failed to pull map context', error))
        .finally(() => {
          this._mapProgressSpinner.hide();
        });
    }, { once: true, capture: false });
  }

  _reinitializeViewExtent (currentExtent, minAllowedScale, maxAllowedScale) {
    const map = this._map;
    const view = map.getView();
    const maxExtent = view.get(ViewProperty.MAX_EXTENT);
    const projection = view.getProjection();
    // Also recalculate min and max resolution as they depend on the map size.
    const { minResolution, maxResolution } = calculateMinMaxResolutions(projection, maxExtent, map.getInnerSize(), minAllowedScale, maxAllowedScale);
    view.set(ViewProperty.MIN_RESOLUTION, minResolution);
    view.set(ViewProperty.MIN_RESOLUTION, maxResolution);
    view.fit(currentExtent, { nearest: true, size: map.getInnerSize() });
  }

  _updateLayers (layers = []) {
    const map = this._map;
    const newLayers = [];
    let layersChanged = false;
    layers.forEach((layerProps, layerIndex) => {
      let existingLayer = this.getLayer(layerProps.id);

      // if layer type changed, remove it from the map and add it as a new layer
      if (existingLayer && (existingLayer.get(LayerProperty.TYPE) !== layerProps[LayerProperty.TYPE]
        || toggleGroupActiveLayerChanged(existingLayer, layerProps))) {
        map.removeLayer(existingLayer);
        layersChanged = true;
        existingLayer = null;
      }

      if (existingLayer && existingLayer.get(LayerProperty.NORMAL_GROUP_CHOICES) !== layerProps[LayerProperty.NORMAL_GROUP_CHOICES]) {
        updateLayer(existingLayer, layerProps);
        layersChanged = true;
        refreshLayer(existingLayer);
      } else if (existingLayer && existingLayer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX) === layerProps[LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX]) {
        const reload = !this.changedLayerIds || this.changedLayerIds.includes(existingLayer.get(LayerProperty.ID));
        updateLayer(existingLayer, layerProps, reload);
      } else {
        const isExistingToggleGroup = existingLayer
          && layerProps[LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX]
          && existingLayer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
        const newLayer = createLayer(layerProps, map);
        context(newLayer).setup(map);
        if (layerIndex === 0 && isExistingToggleGroup) {
          const indexOfOldGroup = this.layers.indexOf(existingLayer);
          map.getLayers().insertAt(indexOfOldGroup + 1, newLayer);
        } else if (layerIndex > 0) {
          const prevLayerId = layers[layerIndex - 1].id;
          const prevLayer = this.getLayer(prevLayerId);
          const prevLayerIndex = this.layers.indexOf(prevLayer);
          map.getLayers().insertAt(prevLayerIndex + 1, newLayer);
        } else {
          map.getLayers().push(newLayer);
        }

        newLayers.push(newLayer);
        layersChanged = true;
      }
    });
    this.layers.forEach((layer) => {
      const id = layerId(layer);
      const activeLayerIndex = layer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
      if (!layers.find((layerProps) => layerProps.id === id && layerProps[LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX] === activeLayerIndex)) {
        map.removeLayer(layer);
        layersChanged = true;
      }
    });

    // reorder layers
    const mapLayersInNewOrder = layers.map(({ id }) => this.getLayer(id));
    const layerOrderChanged = mapLayersInNewOrder.some((layer, index) => index !== this.layers.indexOf(layer));
    if (layerOrderChanged) {
      this._setLayers(mapLayersInNewOrder);
      layersChanged = true;
    }

    if (layersChanged) {
      map.getLayers().changed();
    }

    this._updateMapSelectedLayer(layers);

    // Some lifecycle actions depend on selection, thus this needs to be called afterwards
    // e.g. see feature-acquisition
    newLayers.forEach(layer => executeLayerLifecycleActions(layer, map, 'Open'));

    this.changedLayerIds = null; // Reset after updateLayers().
  }

  _updateMapSelectedLayer (layers) {
    const map = this._map;
    const selectedLayerInModel = layers.find(layer => layer[LAYER_SELECTED_PROPERTY]);
    if (selectedLayerInModel && selectedLayerInModel[LayerProperty.VISIBLE_IN_LAYERLIST]) {
      map.set(MapProperty.SELECTED_LAYER, this.getLayer(selectedLayerInModel[LayerProperty.ID]));
    } else {
      if (!map.get(MapProperty.SELECTED_LAYER)) {
        map.set(MapProperty.SELECTED_LAYER, this.layers.slice().reverse().find(layer => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST)));
      }
      if (selectedLayerInModel || map.get(MapProperty.SELECTED_LAYER)) {
        this._pushIfNotPulling(); // override server state
      }
    }
  }

  pushUnthrottled () {
    return this._pushIfNotPulling();
  }

  _pushIfNotPulling () {
    return this._isPullInProgress ? Promise.resolve() : this._push();
  }

  _push () {
    if (this.#contextPushEnabled) {
      const map = this._map;
      const selectedLayer = this._map.get(MapProperty.SELECTED_LAYER);
      const normalGroupChoices = this.layers.reduce((byLayer, layer) => {
        byLayer[layerId(layer)] = layer.get(LayerProperty.NORMAL_GROUP_CHOICES);
        return byLayer;
      },
      {});
      return this.#mapContextApi.updateContext(
        {
          size: map.getInnerSize(),
          // It is important to subtract padding from the map size and the extent. Otherwise map view will be shifted by the size of the toolbar on each page reload, because padding is taken into account by fitting the map view.
          extent: map.getView().calculateVisibleExtent(),
          scale: this.mapScale,
          layers: this.layers.map(layerIdForPush),
          hiddenLayers: this.layers.filter((layer) => !layer.getVisible()).map(layerId),
          selectedLayer: selectedLayer && layerId(selectedLayer),
          normalGroupChoices
        }
      );
    } else {
      return Promise.resolve();
    }
  }

  getLayer (id) {
    return this.layers.find((layer) => layerId(layer) === id);
  }

  get layers () {
    return [ ...this._map.getLayers().getArray() ];
  }

  _setLayers (layers) {
    // this mutates the layer-array.
    const oldLayers = this._map.getLayers().getArray();
    oldLayers.splice(0, oldLayers.length, ...layers);
  }

  getCanceledLayers () {
    return this.layers.filter(layer => layer.get(LayerProperty.TASKS)?.hasRestartableTasks());
  }

  getOnDemandLayers () {
    return this.layers.filter(layer => layer.getSource().get(SourceProperty.WORKBOOK_VIEW_ON_DEMAND) === WORKBOOK_VIEW_ON_DEMAND.ON);
  }

  hasLayer (layer) {
    return this.layers.indexOf(layer) !== -1;
  }

  set loading (loading) {
    if (loading) {
      this._mapProgressSpinner.show();
    } else {
      this._mapProgressSpinner.hide();
    }
  }

  get loading () {
    return this._mapProgressSpinner.visible;
  }

  cancelLoading () {
    this.layers.forEach(layer => {
      const layerTasks = layer.get(LayerProperty.TASKS);
      if (layerTasks && layerTasks.hasOngoingTasks()) {
        layerTasks.cancelTasks();
      }
    });
  }

  highlightFeatures (features) {
    this._highlightHandler.highlightFeatures(features);
  }

  resetHighlighting () {
    this._highlightHandler.resetHighlighting();
  }

  zoomToLayer (layerId) {
    this.zoomToLayers([ layerId ]);
  }

  zoomToLayers (layerIds) {
    const extent = layerIds
      .map(layerId => this.getLayer(layerId))
      .map(layer => layer && layer.get(LayerProperty.SUGGESTED_EXTENT))
      .filter(Boolean) // filter out undefined layer extents
      .reduce((extent, layerExtent) => extend(extent, layerExtent), createEmpty());

    if (!isEmpty(extent)) {
      this._map.getView().fit(extent);
    }
  }

  get mapScale () {
    const view = this._map.getView();
    if (!view.getResolution()) {
      view.setResolution(view.getResolutionForZoom(view.getZoom()));
    }
    return scaleForResolution(view, view.getResolution());
  }

  set mapScale (scale) {
    const view = this._map.getView();
    view.setResolution(resolutionForScale(view, scale));
  }

  getCurrentPrimaryInteractionIfResettable () {
    const primaryInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION);
    return isResettableMapInteraction(primaryInteraction) ? primaryInteraction : null;
  }

  getProjection () {
    return this._map.getView().getProjection();
  }

}

MapContext.prototype.push = throttle(MapContext.prototype._pushIfNotPulling, 500);

function layerIdForPush (layer) {
  const id = layer.get(LayerProperty.ID);
  const activeLayerIndex = layer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
  return activeLayerIndex != null ? `${id}:${activeLayerIndex}` : id;
}

function layerId (layer) {
  return layer.get(LayerProperty.ID);
}

function executeLayerLifecycleActions (layer, map, lifecyclePhase) {
  const lifecycleActions = layer.get(LayerProperty.LIFECYCLE_ACTIONS);
  if (lifecycleActions) {
    const layerOpenLifecycleActions = lifecycleActions[lifecyclePhase];
    if (layerOpenLifecycleActions && layerOpenLifecycleActions.length) {
      const allLayerActionsMapping = map.get(MapProperty.LAYER_ACTIONS_PROVIDER)(layer);
      layerOpenLifecycleActions.forEach(lifecycleAction => {
        const layerLifecycleAction = allLayerActionsMapping[lifecycleAction];
        if (layerLifecycleAction) {
          layerLifecycleAction.isSupported(getActionContext(map)).then(supported => {
            if (supported) {
              layerLifecycleAction.execute();
            }
          }).catch(err => logger.error('Could not check if lifecycleAction is supported ', err));
        }
      });
    }
  }
}

function calculateMinMaxResolutions (projection, maxExtent, mapSize, minAllowedScale, maxAllowedScale) {
  // Dummy view is needed to calculate maxResolution for the real map view. There is no other way to supply maxResolution after View object construction.
  const dummyView = new View({ projection });
  let maxResolution = dummyView.getResolutionForExtent(maxExtent, mapSize);
  /*
      Based on how OL2 calculates min resolution - relevant OL2 code can be found here:
      https://github.com/openlayers/ol2/blob/master/lib/OpenLayers/Layer.js#L838
      Current calculation is based on expectation that the maxExtent option was the only relevant attribute (option)
      initially passed to OL2 in cadenza application.
  */
  let minResolution = maxResolution / Math.pow(ZOOM_SCALE_FACTOR, DEFAULT_NUMBER_OF_ZOOM_LEVELS - 1);

  // For Web Mercator projections maps, maxResolution is adjusted to match standard web mercator zoom levels and avoid map image scaling.
  // minResolution is always the same - the 25th web mercator zoom level
  if (isWebMercatorProjection(projection)) {
    const maxWebMercatorResolution = getWebMercatorMaxResolution();
    let adjustedMaxResolution = maxWebMercatorResolution;
    while (adjustedMaxResolution > maxResolution) {
      adjustedMaxResolution /= ZOOM_SCALE_FACTOR;
    }
    maxResolution = roundToFloatOrInt(adjustedMaxResolution, 3);
    minResolution = roundToFloatOrInt(maxWebMercatorResolution / Math.pow(ZOOM_SCALE_FACTOR, DEFAULT_NUMBER_OF_ZOOM_LEVELS - 1), 3);
  }
  // overwrite any calculated values if the min/maxAllowedScale is set in the mml file
  if (isMinAllowedScaleSet(minAllowedScale)) {
    minResolution = scaleToResolution(minAllowedScale);
  }
  if (isMaxAllowedScaleSet(maxAllowedScale)) {
    maxResolution = scaleToResolution(maxAllowedScale);
  }

  return { minResolution, maxResolution };
}

function isMinAllowedScaleSet (minAllowedScale) {
  return minAllowedScale !== null && minAllowedScale !== undefined && minAllowedScale !== 0;
}

function isMaxAllowedScaleSet (maxAllowedScale) {
  return maxAllowedScale !== null && maxAllowedScale !== undefined && maxAllowedScale !== 0 && maxAllowedScale < Number.MAX_VALUE;
}
